import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Search, Plus, Users, Loader2, Pencil, Trash2, Copy, Check, Package } from 'lucide-react';
import { listarContatos, criarContato, atualizarContato, excluirContato, buscarCep, enviosDoContato, listarContatosIntegracao, enviosContatoIntegracao } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const vazio = { nome: '', cpf_cnpj: '', telefone: '', email: '', observacao: '', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };

const fmtCep = (v) => String(v || '').replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');

const Campo = ({ label, children }) => (
  <label className="block"><span className="text-xs font-medium text-slate-600">{label}</span>{children}</label>
);

const COR_STATUS = {
  'Aguardando postagem': 'bg-amber-50 text-amber-700', 'Em trânsito': 'bg-blue-50 text-blue-700', 'Saiu para entrega': 'bg-indigo-50 text-indigo-700',
  'Entregue': 'bg-emerald-50 text-emerald-700', 'Cancelado': 'bg-slate-100 text-slate-500', 'Cancelando': 'bg-slate-100 text-slate-500',
};
const fmtData = (d) => { try { return new Date(d.endsWith && !d.endsWith('Z') && !d.includes('+') ? d + 'Z' : d).toLocaleDateString('pt-BR', { timeZone: 'America/Fortaleza' }); } catch { return '-'; } };
const fmtValor = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Janela com os envios (códigos de rastreio) feitos para o cliente
const EnviosDoCliente = ({ cliente, integracao, onClose }) => {
  const [envios, setEnvios] = useState(null);
  const [copiado, setCopiado] = useState(null);
  useEffect(() => {
    if (!cliente) return;
    setEnvios(null);
    (integracao ? enviosContatoIntegracao(cliente.id) : enviosDoContato(cliente.id)).then(setEnvios).catch(() => setEnvios([]));
  }, [cliente, integracao]);
  const copiar = (c) => { try { navigator.clipboard.writeText(c); setCopiado(c); setTimeout(() => setCopiado(null), 1500); } catch { /* noop */ } };
  return (
    <Dialog open={!!cliente} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{cliente?.nome}</DialogTitle>
          <DialogDescription>{cliente ? `${cliente.endereco.cidade}/${cliente.endereco.uf}${cliente.cpf_cnpj ? ' · ' + cliente.cpf_cnpj : ''} — envios e códigos de rastreio` : ''}</DialogDescription>
        </DialogHeader>
        {envios === null ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : envios.length === 0 ? (
          <div className="py-10 text-center text-slate-400"><Package className="w-10 h-10 mx-auto mb-2 opacity-40" />Nenhum envio encontrado para este cliente.</div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
            {envios.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                <div className="min-w-[150px]">
                  {e.codigo_objeto ? (
                    <span className="inline-flex items-center gap-1.5">
                      <a href={`/rastreio?codigo=${e.codigo_objeto}`} className="font-mono font-semibold text-[#2A5BC7] hover:underline">{e.codigo_objeto}</a>
                      <button type="button" onClick={() => copiar(e.codigo_objeto)} className="text-slate-400 hover:text-[#2A5BC7]" aria-label="Copiar código">
                        {copiado === e.codigo_objeto ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </span>
                  ) : <span className="text-slate-400">sem código</span>}
                  <span className="block text-xs text-slate-500">{fmtData(e.created_at)} · {e.servico_nome || e.servico}</span>
                </div>
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${COR_STATUS[e.status] || 'bg-slate-100 text-slate-600'}`}>{e.status}</span>
                <span className="ml-auto text-right">
                  <span className="font-semibold text-slate-800">{fmtValor(e.valor)}</span>
                  {e.pedido_gestao && <span className="block text-xs text-slate-500">Pedido {e.pedido_gestao} no Gerenciador</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ClientesPage = () => {
  const { user } = useAuth();
  const ehAdmin = !!(user && user.is_admin);
  const [aba, setAba] = useState('meus');
  const [verCliente, setVerCliente] = useState(null);
  const [lista, setLista] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(vazio);
  const [erro, setErro] = useState(null);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async (busca) => {
    setLoading(true);
    try { setLista(await (aba === 'gestao' ? listarContatosIntegracao(busca) : listarContatos(busca))); } catch { setLista([]); } finally { setLoading(false); }
  }, [aba]);

  useEffect(() => {
    const t = setTimeout(() => carregar(q), 250);
    return () => clearTimeout(t);
  }, [q, carregar]);
  const integracao = aba === 'gestao';

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const novo = () => { setEditId(null); setF(vazio); setErro(null); setAberto(true); };
  const editar = (c) => {
    setEditId(c.id);
    setF({ ...vazio, nome: c.nome, cpf_cnpj: c.cpf_cnpj || '', telefone: c.telefone || '', email: c.email || '', observacao: c.observacao || '', ...c.endereco, complemento: c.endereco.complemento || '' });
    setErro(null); setAberto(true);
  };

  const aoSairCep = async () => {
    const r = await buscarCep(f.cep);
    if (r) setF((cur) => ({ ...cur, logradouro: cur.logradouro || r.logradouro, bairro: cur.bairro || r.bairro, cidade: r.cidade, uf: r.uf }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    setBusy(true); setErro(null);
    const corpo = {
      nome: f.nome, cpf_cnpj: f.cpf_cnpj || null, telefone: f.telefone || null, email: f.email || null, observacao: f.observacao || null,
      endereco: { cep: f.cep, logradouro: f.logradouro, numero: f.numero, complemento: f.complemento || null, bairro: f.bairro, cidade: f.cidade, uf: f.uf },
    };
    try {
      if (editId) await atualizarContato(editId, corpo); else await criarContato(corpo);
      setAberto(false);
      await carregar(q);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Confira os campos obrigatórios.');
    } finally { setBusy(false); }
  };

  const apagar = async (c) => {
    if (!window.confirm(`Apagar ${c.nome} da sua carteira de clientes? Os envios já feitos não são afetados.`)) return;
    await excluirContato(c.id);
    await carregar(q);
  };

  return (
    <AppShell title="Meus clientes" subtitle="Destinatários salvos automaticamente a cada envio — clique num cliente para ver os envios e códigos de rastreio">
      {ehAdmin && (
        <div className="inline-flex bg-slate-100 rounded-full p-1 mb-4">
          {[['meus', 'Meus clientes'], ['gestao', 'Gestão integrada']].map(([k, rot]) => (
            <button key={k} type="button" onClick={() => { setAba(k); setQ(''); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${aba === k ? 'bg-white text-[#2A5BC7] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>{rot}</button>
          ))}
        </div>
      )}
      {integracao && <p className="text-sm text-slate-500 mb-3">Clientes dos envios emitidos pelo sistema de gestão (gerenciadorinnova.app). Só consulta.</p>}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar por nome, documento, cidade ou CEP" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          {!integracao && <Button onClick={novo} className="inn-btn-primary rounded-full h-10 px-5"><Plus className="w-4 h-4 mr-1" /> Novo cliente</Button>}
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : lista.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-semibold text-slate-700">{q ? 'Nenhum cliente encontrado' : 'Nenhum cliente salvo ainda'}</p>
            <p className="text-sm mt-1">{q ? 'Tente outra busca.' : 'Cada destinatário dos seus envios é salvo aqui automaticamente.'}</p>
            {!q && <Link to="/calcular"><Button className="mt-5 inn-btn-primary rounded-full">Criar um envio</Button></Link>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Nome</th>
                  <th className="text-left px-5 py-3 font-semibold">Documento</th>
                  <th className="text-left px-5 py-3 font-semibold">Cidade/UF</th>
                  <th className="text-left px-5 py-3 font-semibold">Telefone</th>
                  <th className="text-right px-5 py-3 font-semibold">Envios</th>
                  <th className="text-right px-5 py-3 font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lista.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setVerCliente(c)} title="Ver envios e códigos de rastreio">
                    <td className="px-5 py-3 font-medium text-slate-900">{c.nome}<span className="block text-xs text-slate-500 font-normal">{c.endereco.logradouro}, {c.endereco.numero}</span></td>
                    <td className="px-5 py-3 text-slate-600">{c.cpf_cnpj || '-'}</td>
                    <td className="px-5 py-3 text-slate-600">{c.endereco.cidade}/{c.endereco.uf}<span className="block text-xs text-slate-500">{fmtCep(c.endereco.cep)}</span></td>
                    <td className="px-5 py-3 text-slate-600">{c.telefone || '-'}</td>
                    <td className="px-5 py-3 text-right"><span className="inline-flex items-center gap-1 text-[#2A5BC7] font-semibold"><Package className="w-3.5 h-3.5" />{c.envios || 0}</span></td>
                    <td className="px-5 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {integracao ? <button onClick={() => setVerCliente(c)} className="text-[#2A5BC7] hover:text-[#F77820] text-xs font-semibold">Ver envios</button> : (<>
                        <button onClick={() => editar(c)} className="text-[#2A5BC7] hover:text-[#F77820] mr-3" aria-label="Editar"><Pencil className="w-4 h-4 inline" /></button>
                        <button onClick={() => apagar(c)} className="text-red-500 hover:text-red-700" aria-label="Apagar"><Trash2 className="w-4 h-4 inline" /></button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EnviosDoCliente cliente={verCliente} integracao={integracao} onClose={() => setVerCliente(null)} />

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar cliente' : 'Novo cliente'}</DialogTitle>
            <DialogDescription>Os dados ficam só na sua carteira de clientes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={salvar} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Campo label="Nome *"><Input className="mt-1" value={f.nome} onChange={set('nome')} maxLength={50} required /></Campo>
              <Campo label="CPF ou CNPJ"><Input className="mt-1" value={f.cpf_cnpj} onChange={set('cpf_cnpj')} /></Campo>
              <Campo label="Telefone (com DDD)"><Input className="mt-1" value={f.telefone} onChange={set('telefone')} /></Campo>
              <Campo label="E-mail"><Input className="mt-1" type="email" value={f.email} onChange={set('email')} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="CEP *"><Input className="mt-1" value={fmtCep(f.cep)} onChange={set('cep')} onBlur={aoSairCep} required /></Campo>
              <div className="col-span-2"><Campo label="Rua *"><Input className="mt-1" value={f.logradouro} onChange={set('logradouro')} maxLength={50} required /></Campo></div>
              <Campo label="Número *"><Input className="mt-1" value={f.numero} onChange={set('numero')} maxLength={10} required /></Campo>
              <div className="col-span-2"><Campo label="Complemento"><Input className="mt-1" value={f.complemento} onChange={set('complemento')} maxLength={30} /></Campo></div>
              <div className="col-span-3"><Campo label="Bairro *"><Input className="mt-1" value={f.bairro} onChange={set('bairro')} maxLength={30} required /></Campo></div>
              <div className="col-span-2"><Campo label="Cidade *"><Input className="mt-1" value={f.cidade} onChange={set('cidade')} maxLength={30} required /></Campo></div>
              <Campo label="UF *"><Input className="mt-1 uppercase" value={f.uf} onChange={set('uf')} maxLength={2} required /></Campo>
            </div>
            <Campo label="Observação"><Input className="mt-1" value={f.observacao} onChange={set('observacao')} maxLength={200} /></Campo>
            {erro && <p className="text-sm text-red-600">{erro}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setAberto(false)}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="inn-btn-primary rounded-full px-6">{busy ? 'Salvando...' : 'Salvar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default ClientesPage;
